/**
 * Shared constants and utilities for dashboard filtering
 */

/** Maps dashboard filter types to URL query param names */
export const FILTER_PARAM_MAP: Record<string, string> = {
    source: 'source',
    referringSource: 'referringSource',
    contactType: 'contactType',
    serviceType: 'serviceType',
    jobType: 'jobType',
    urgency: 'urgency',
    highValueLead: 'highValueLead',
    zipCode: 'zipCode',
    // Lead taxonomy filters. The dashboard query gained these arguments in stentor-api#6042; they
    // are deliberately absent from LEADS_FILTER_MAP below until the leads list reads them, so the
    // "View Leads" handoff doesn't emit a param that page ignores.
    contactMethod: 'contactMethod',
    agentType: 'agentType',
    capturePlatform: 'capturePlatform',
    sourceSystem: 'sourceSystem'
};

/** Maps dashboard filter types to leads list query param names */
export const LEADS_FILTER_MAP: Record<string, string> = {
    source: 'source',
    referringSource: 'referringSource',
    urgency: 'urgency',
    highValueLead: 'highValueLead',
    contactType: 'contactType',
    serviceType: 'serviceType',
    jobType: 'jobType',
    zipCode: 'zipCode'
};

/** Maximum allowed length for filter values */
const MAX_FILTER_VALUE_LENGTH = 200;

/** Pattern for valid filter values - alphanumeric, underscores, hyphens, spaces */
const VALID_FILTER_PATTERN = /^[a-zA-Z0-9_\s-]+$/;

/**
 * Validates a filter value from URL params
 * Returns undefined if the value is invalid or empty
 */
export const validateFilterValue = (value: string | null): string | undefined => {
    if (!value) return undefined;
    if (value.length > MAX_FILTER_VALUE_LENGTH) return undefined;
    if (!VALID_FILTER_PATTERN.test(value)) return undefined;
    return value;
};

/**
 * Validates an organization ID
 * Must be alphanumeric with hyphens/underscores, 1-128 chars
 */
export const isValidOrganizationId = (id: string): boolean => {
    return typeof id === 'string' && id.length > 0 && id.length <= 128 && /^[a-zA-Z0-9_-]+$/.test(id);
};

/**
 * Validates an app ID
 * Must be alphanumeric with hyphens/underscores, 1-128 chars
 */
export const isValidAppId = (id: string): boolean => {
    return typeof id === 'string' && id.length > 0 && id.length <= 128 && /^[a-zA-Z0-9_-]+$/.test(id);
};

/**
 * Parses boolean filter value from URL param
 */
export const parseBooleanFilter = (value: string | null): boolean | undefined => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
};

/** Days in the default dashboard range. */
export const DEFAULT_DASHBOARD_DAYS_BACK = 30;

/**
 * The default dashboard range: the trailing 30 days ending now, the same range the date filter's
 * "Last 30 Days" quick select picks, so the filter labels the default correctly.
 */
export const defaultDashboardDateRange = (now: Date = new Date()): { start: string; end: string } => {
    const start = new Date(now);
    start.setDate(start.getDate() - DEFAULT_DASHBOARD_DAYS_BACK);
    return { start: start.toISOString(), end: now.toISOString() };
};

/** A date param must at least start with YYYY-MM-DD; Date.parse alone accepts strings like "1". */
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}/;

/**
 * Returns the date param unchanged if it is a real date, otherwise undefined. "2026-02-31" parses
 * (it rolls over), so the calendar day is checked against what it parsed to.
 */
export const parseDateParam = (value: string | null | undefined): string | undefined => {
    if (!value || !DATE_PARAM_PATTERN.test(value)) return undefined;
    const time = Date.parse(value);
    if (Number.isNaN(time)) return undefined;
    const day = value.slice(0, 10);
    // A date-only value parses as UTC midnight; compare in UTC so the day can't shift.
    if (value.length === 10 && new Date(time).toISOString().slice(0, 10) !== day) return undefined;
    return value;
};

/**
 * The date range a dashboard should query, from its URL params. A param that is not a date falls
 * back to the default, and a reversed range falls back to the default range, so a bad URL never
 * reaches the API (which would fail the whole page) and never crashes the render.
 */
export const resolveDashboardDateRange = (
    startParam: string | null | undefined,
    endParam: string | null | undefined,
    defaults: { start: string; end: string }
): { start: string; end: string } => {
    const start = parseDateParam(startParam) || defaults.start;
    const end = parseDateParam(endParam) || defaults.end;
    if (Date.parse(start) > Date.parse(end)) {
        return defaults;
    }
    return { start, end };
};
